import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Divider,
  Button,
  Alert,
  CircularProgress,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControlLabel,
  Card,
  CardContent,
  CardActions,
  Stack,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import api from '../../services/api';
import MainLayout from '../layout/MainLayout';
import IncompleteRegistration from './IncompleteRegistration';
import { useAuth } from '../../context/AuthContext';
import { useFestival } from '../../context/FestivalContext';
import StyledButton from '../common/StyledButton';
import { formatCheckInDateLabel } from '../../utils/festivalDates';

// Session flag prevents an infinite redirect loop when the user clicks Back
// from /vendor/onboarding to /vendor/dashboard while still missing an
// enrollment for the active festival.
const AUTO_ROUTE_FLAG = 'gt:auto_routed_onboarding';

// Classify the user's relationship to the active festival so the dashboard
// can either auto-route them into onboarding or surface a clear CTA when
// they return to the dashboard later.
const classifyEnrollmentState = (vendorList) => {
  if (!Array.isArray(vendorList) || vendorList.length === 0) {
    return 'no_vendor_row';
  }
  const hasAnyCurrentEnrollment = vendorList.some(
    (v) => v.registration_step || v.enrollment_status
  );
  if (!hasAnyCurrentEnrollment) {
    return 'no_current_year_enrollment';
  }
  return 'has_enrollment';
};

const VendorDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [vendors, setVendors] = useState([]);
  const [boothRegistrations, setBoothRegistrations] = useState([]);
  const [registrationStatuses, setRegistrationStatuses] = useState({});
  const [selectedBooths, setSelectedBooths] = useState([]);
  const [enrollmentState, setEnrollmentState] = useState(null);
  const { user } = useAuth();
  const { selected: selectedFestival } = useFestival();
  const eventDate = selectedFestival?.event_date || null;
  const festivalLabel = selectedFestival
    ? selectedFestival.name || `${selectedFestival.year} Gingham Tree`
    : 'this year';
  const [success, setSuccess] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchVendorData = async () => {
    try {
      console.log('Fetching vendor data...');
      const response = await fetch('/api/vendor/registration-status', {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Failed to fetch vendor data');
      }
      const data = await response.json();
      
      console.log('Received vendor data:', {
        count: data.length,
        vendors: data
      });
      
      // Set vendors from the transformed data
      setVendors(data);
      setEnrollmentState(classifyEnrollmentState(data));
      
      // Convert booth registrations to status map
      const statusMap = {};
      data.forEach(vendor => {
        vendor.registrations.forEach(reg => {
          statusMap[reg.booth_id] = {
            registration_status: reg.registration_status,
            payment_status: reg.payment_status
          };
        });
      });
      
      console.log('Created status map:', statusMap);
      setRegistrationStatuses(statusMap);
    } catch (err) {
      console.error('Error fetching vendor data:', err);
      setError(
        "We couldn't load your dashboard. Click Retry, or refresh the page. If the issue keeps happening, log out and back in."
      );
    }
  };

  const fetchBoothRegistrations = async () => {
    try {
      console.log('Fetching booth registrations...');
      const response = await fetch('/api/vendor/booth-registration', {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Failed to fetch booth registrations');
      }
      const data = await response.json();
      
      console.log('Received booth registrations:', {
        raw: data,
        count: data.length
      });
      
      // Transform the data to include all necessary information
      const transformedData = data.map(booth => ({
        id: booth.id,
        booth_id: booth.booth_id,
        vendor_id: booth.vendor_id,
        cell_name: booth.cell_name,
        is_corner: booth.is_corner,
        needs_electricity: booth.needs_electricity,
        special_requests: booth.special_requests,
        registration_status: booth.status,
        payment_status: booth.payment_status,
        section: booth.section,
      }));
      
      console.log('Transformed booth data:', {
        transformed: transformedData,
        count: transformedData.length
      });
      
      setBoothRegistrations(transformedData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching booth registrations:', err);
      setError(
        "We couldn't load your booth list. Click Retry, or refresh the page. If the issue keeps happening, log out and back in."
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('VendorDashboard mounted, fetching data...');
    fetchVendorData();
    fetchBoothRegistrations();
  }, []);

  // Check for success message from payment completion
  useEffect(() => {
    if (location.state?.message) {
      setSuccess(location.state.message);
      // Clear the message from location state
      navigate(location.pathname, { replace: true });
      // Refresh the data
      fetchVendorData();
      fetchBoothRegistrations();
    }
  }, [location.state, navigate]);

  // First-visit auto-route: a user who landed here without an enrollment for
  // the active festival (either brand-new abandoned signup, or returning
  // vendor whose new-year enrollment hasn't been created yet) should be
  // dropped straight into /vendor/onboarding so the next step is obvious.
  // The session flag prevents a redirect loop if they click Back.
  useEffect(() => {
    if (loading) return;
    if (!enrollmentState) return;
    if (enrollmentState === 'has_enrollment') return;
    if (typeof window === 'undefined') return;
    if (window.sessionStorage.getItem(AUTO_ROUTE_FLAG)) return;
    window.sessionStorage.setItem(AUTO_ROUTE_FLAG, '1');
    navigate('/vendor/onboarding', { replace: true });
  }, [loading, enrollmentState, navigate]);

  // Add auto-refresh after payment
  useEffect(() => {
    let timeoutId;
    if (location.state?.paymentStatus === 'succeeded') {
      // Refresh data every 2 seconds for 10 seconds to ensure we get updated status
      let attempts = 0;
      const refreshData = () => {
        if (attempts < 5) {
          fetchVendorData();
          fetchBoothRegistrations();
          attempts++;
          timeoutId = setTimeout(refreshData, 2000);
        }
      };
      refreshData();
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [location.state?.paymentStatus]);

  const formatCheckInDate = (date) => formatCheckInDateLabel(eventDate, date);

  const handleBoothSelect = (booth) => {
    setSelectedBooths(prev => {
      if (prev.includes(booth.id)) {
        return prev.filter(id => id !== booth.id);
      }
      return [...prev, booth.id];
    });
  };

  const handlePaySelected = (vendorId) => {
    const selectedBoothData = boothRegistrations.filter(booth => 
      selectedBooths.includes(booth.id) && booth.vendor_id === vendorId
    );
    navigate('/vendor/payment', { 
      state: { 
        boothData: selectedBoothData,
        vendorData: vendors.find(v => v.id === vendorId),
        isMultiBoothPayment: true
      }
    });
  };

  // Vendor-initiated removal of an editable booth registration. Backend
  // returns 409 for approved/complete rows; we mirror that in the UI by
  // disabling the trigger for those statuses, but still surface the error
  // if it slips through (e.g. status changed since the page loaded).
  const handleConfirmDelete = async () => {
    if (!deleteDialog) return;
    setDeleting(true);
    try {
      await api.post(`/vendor/booth-registration/${deleteDialog.id}/release`);
      setDeleteDialog(null);
      setSuccess(`Booth ${deleteDialog.cell_name} removed from your registration.`);
      await Promise.all([fetchVendorData(), fetchBoothRegistrations()]);
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        'Could not remove this booth registration. Please try again or contact an admin.';
      setError(message);
      setDeleteDialog(null);
    } finally {
      setDeleting(false);
    }
  };

  // Calculate total for selected booths for a specific vendor.
  const calculateTotal = (booths, vendorId) => {
    return booths
      .filter(booth => booth.vendor_id === vendorId && selectedBooths.includes(booth.id))
      .reduce((total, booth) => {
        return total + (booth.is_corner ? 80 : 70) + (booth.needs_electricity ? 5 : 0);
      }, 0);
  };

  // Render summary for a specific booth registration
  const renderBoothSummary = (booth, vendorId) => {
    const registrationStatus = booth.registration_status;
    const paymentStatus = booth.payment_status;

    const isApproved = registrationStatus === 'approved';
    const isPending = registrationStatus === 'pending_approval';
    const isComplete = registrationStatus === 'complete' || paymentStatus === 'succeeded';
    const isDenied = registrationStatus === 'denied';
    const isExpired = registrationStatus === 'expired';

    // Vendors can delete pre-approval and denied rows themselves. Approved
    // and complete rows are admin-only territory because money may be
    // involved.
    const canDelete = [
      'soft_held',
      'pending_approval',
      'incomplete',
      'denied',
    ].includes(registrationStatus);

    return (
      <Paper
        elevation={0}
        sx={{
          p: isMobile ? 2 : 3,
          mb: 2,
          backgroundColor: '#fff',
          borderLeft: 4,
          borderLeftColor: isComplete ? 'success.main' :
                          isApproved ? 'primary.main' :
                          isPending ? 'warning.main' : 'grey.300'
        }}
        key={booth.id}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ color: '#1a237e', fontWeight: 600 }}>
              Booth {booth.cell_name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {booth.is_corner ? 'Corner Booth' : 'Regular Booth'} •
              {booth.needs_electricity ? ' With Electricity' : ' No Electricity'} •
              Section {booth.section}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                color: isComplete ? 'success.main' :
                        isApproved ? 'primary.main' :
                        isPending ? 'warning.main' : 'text.secondary'
              }}
            >
              Status: {isComplete ? 'Complete' :
                      isApproved ? 'Approved - Pending Payment' :
                      isPending ? 'Pending Approval' :
                      isDenied ? 'Denied' :
                      isExpired ? 'Released' : 'Processing'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: isMobile ? 1 : 0 }}>
            {isApproved && !isComplete && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedBooths.includes(booth.id)}
                    onChange={() => handleBoothSelect(booth)}
                    color="primary"
                    size={isMobile ? "small" : "medium"}
                  />
                }
                label={
                  isMobile ? 'Select' : 'Select for payment'
                }
              />
            )}

            <Tooltip
              title={
                canDelete
                  ? 'Remove this booth registration'
                  : 'Approved or paid booths can only be removed by an admin.'
              }
            >
              <span>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteOutlineIcon fontSize="small" />}
                  onClick={() => setDeleteDialog(booth)}
                  disabled={!canDelete}
                >
                  Delete
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Paper>
    );
  };

  // Render a vendor card with its booths
  const renderVendorCard = (vendor) => {
    console.log('Rendering vendor card:', {
      vendor_id: vendor.id || vendor.vendor_id,
      business_name: vendor.business_name,
      vendor: vendor
    });

    const vendorId = vendor.id || vendor.vendor_id;
    const vendorBooths = boothRegistrations.filter(b => b.vendor_id === vendorId);
    console.log('Found booths for vendor:', {
      vendor_id: vendorId,
      booth_count: vendorBooths.length,
      booths: vendorBooths
    });

    // "Approved & unpaid" for the pay CTA.
    const approvedBooths = vendorBooths.filter(b => {
      if (b.registration_status !== 'approved') return false;
      return b.payment_status !== 'succeeded';
    });
    console.log('Approved unpaid booths:', {
      vendor_id: vendorId,
      approved_count: approvedBooths.length,
      approved_booths: approvedBooths
    });

    const selectedVendorBooths = selectedBooths.filter(id => 
      vendorBooths.some(b => b.id === id)
    );

    return (
      <Card 
        key={vendorId} 
        elevation={1} 
        sx={{ 
          mb: 3,
          backgroundColor: '#fafbfc',
          borderRadius: 2
        }}
      >
        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: isMobile ? 'flex-start' : 'center',
            flexDirection: isMobile ? 'column' : 'row',
            mb: 2
          }}>
            <Box>
              <Typography variant={isMobile ? "h6" : "h5"} sx={{ color: '#1a237e', fontWeight: 600 }}>
                {vendor.business_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {vendor.description}
              </Typography>
            </Box>
            {enrollmentState === 'has_enrollment' && (
              <Box sx={{ mt: isMobile ? 2 : 0 }}>
                <StyledButton
                  variant="outlined"
                  size={isMobile ? "small" : "medium"}
                  onClick={() => navigate('/vendor/onboarding?step=booth_selection')}
                >
                  Add Booth
                </StyledButton>
              </Box>
            )}
          </Box>

          {vendorBooths.length > 0 ? (
            <>
              <Typography variant="subtitle1" sx={{ mb: 2, color: '#1a237e' }}>
                Booth Registrations
              </Typography>
              {approvedBooths.length > 0 && selectedVendorBooths.length > 0 && (
                <Paper sx={{ 
                  mb: 2, 
                  p: isMobile ? 2 : 3, 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: 1 
                }}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Total for selected booths: ${calculateTotal(vendorBooths, vendorId)}
                  </Typography>
                  <StyledButton
                    variant="contained"
                    color="primary"
                    size={isMobile ? "small" : "medium"}
                    onClick={() => handlePaySelected(vendorId)}
                  >
                    Pay for Selected ({selectedVendorBooths.length})
                  </StyledButton>
                </Paper>
              )}
              {vendorBooths.map(booth => renderBoothSummary(booth, vendorId))}
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {enrollmentState === 'has_enrollment'
                  ? 'No booths registered yet'
                  : `Last year's profile shown above. Start your ${festivalLabel} registration to pick booths.`}
              </Typography>
              {enrollmentState === 'has_enrollment' && (
                <StyledButton
                  variant="contained"
                  size={isMobile ? 'small' : 'medium'}
                  onClick={() => navigate('/vendor/onboarding?step=booth_selection')}
                >
                  Register a Booth
                </StyledButton>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    console.log('VendorDashboard is loading...');
    return (
      <MainLayout>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
          <CircularProgress />
        </Container>
      </MainLayout>
    );
  }

  console.log('VendorDashboard rendering with:', {
    vendor_count: vendors.length,
    booth_count: boothRegistrations.length,
    vendors,
    boothRegistrations
  });

  return (
    <MainLayout>
      <Container 
        maxWidth="lg" 
        sx={{ 
          mt: isMobile ? 2 : 4, 
          mb: isMobile ? 2 : 4,
          px: isMobile ? 1 : 3
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          mb: isMobile ? 2 : 4
        }}>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            component="h1" 
            sx={{ 
              color: '#1a237e', 
              fontWeight: 600,
              mb: isMobile ? 2 : 0
            }}
          >
            Vendor Dashboard
          </Typography>
          {enrollmentState === 'has_enrollment' && (
            <StyledButton
              variant="contained"
              startIcon={<AddBusinessIcon />}
              size={isMobile ? 'small' : 'medium'}
              onClick={() => navigate('/vendor/onboarding')}
            >
              Add New Business
            </StyledButton>
          )}
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setError('');
                  setLoading(true);
                  fetchVendorData();
                  fetchBoothRegistrations();
                }}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Prominent CTA when the user has no enrollment for the active */}
        {/* festival. The auto-redirect effect above sends them to onboarding */}
        {/* on first visit; this card is what they see on subsequent visits. */}
        {enrollmentState === 'no_vendor_row' && <IncompleteRegistration />}
        {enrollmentState === 'no_current_year_enrollment' && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4 },
              mb: 3,
              backgroundColor: '#fffbe7',
              border: '1px solid #f0d96a',
              borderRadius: 2,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a237e', mb: 1 }}>
              Welcome back! Start your {festivalLabel} registration
            </Typography>
            <Typography variant="body1" sx={{ color: '#555', mb: 3 }}>
              Your vendor profile from a previous year is still on file. Update
              it for {festivalLabel} and pick your booths to finish registering.
            </Typography>
            <StyledButton
              variant="contained"
              size={isMobile ? 'small' : 'large'}
              onClick={() => navigate('/vendor/onboarding')}
            >
              Begin {festivalLabel} Registration
            </StyledButton>
          </Paper>
        )}

        {vendors.length > 0 && (
          <>
            {vendors.map(vendor => renderVendorCard(vendor))}
          </>
        )}

        <Dialog
          open={Boolean(deleteDialog)}
          onClose={() => (deleting ? null : setDeleteDialog(null))}
        >
          <DialogTitle>Remove booth registration?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This frees up <strong>Booth {deleteDialog?.cell_name}</strong> for other vendors.
              You can pick a new booth again from the dashboard.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              disabled={deleting}
            >
              {deleting ? 'Removing...' : 'Remove booth'}
            </Button>
          </DialogActions>
        </Dialog>

      </Container>
    </MainLayout>
  );
};

export default VendorDashboard; 